import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { Avatar } from '@/components/common/Avatar'
import { MembershipCard } from '@/components/membership/MembershipCard'
import { useMyMembership, useMyThresholds } from '@/hooks/useMembership'
import { certificateService } from '@/services/certificateService'
import { authService } from '@/services/authService'
import { apiClient } from '@/services/api'
import { ktpService, type KTPStatusResponse } from '@/services/ktpService'
import { uploadService } from '@/services/uploadService'
import { useResetUserTOTP } from '@/hooks/useUsers'
import type { Certificate } from '@/types/certificate'

export const ProfilePage: React.FC = () => {
    useTranslation('common')
    const { user, logout, updateUser } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const compact = location.pathname.startsWith('/admin') || location.pathname.startsWith('/instructor')
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Helper to format ISO date string to YYYY-MM-DD for <input type="date" />
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return ''
        return dateStr.split('T')[0]
    }

    // Form States
    const [name, setName] = useState(user?.name || '')
    const [gender, setGender] = useState<'' | 'L' | 'P'>((user as any)?.gender || '')
    const [birthDate, setBirthDate] = useState<string>(formatDate(user?.birthDate))
    
    // Sync form states with user object
    useEffect(() => {
        if (user) {
            setName(user.name || '')
            setGender(user.gender || '')
            setBirthDate(formatDate(user.birthDate))
        }
    }, [user])
    
    const [isUploading, setIsUploading] = useState(false)
    const [savingIdentity, setSavingIdentity] = useState(false)
    const [identityStatus, setIdentityStatus] = useState<{ type: 'success' | 'error' | 'idle', text?: string }>({ type: 'idle' })

    // Password Modal States
    const [passwordModalOpen, setPasswordModalOpen] = useState(false)
    const [passData, setPassData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })
    const [passSubmitting, setPassSubmitting] = useState(false)
    const [passError, setPassError] = useState<string | null>(null)

    // Email Modal States
    const [emailModalOpen, setEmailModalOpen] = useState(false)
    const [newEmail, setNewEmail] = useState('')
    const [emailSubmitting, setEmailSubmitting] = useState(false)
    const [emailError, setEmailError] = useState<string | null>(null)

    // Reset TOTP Modal States
    const [resetTOTPModalOpen, setResetTOTPModalOpen] = useState(false)
    const [resetTOTPError, setResetTOTPError] = useState<string | null>(null)
    const resetTOTPMutation = useResetUserTOTP()

    const isStudent = user?.role === 'student'

    // Queries
    const { data: certificates = [] } = useQuery<Certificate[]>({
        queryKey: ['certificates', user?.id],
        queryFn: () => (user?.id ? certificateService.getMyCertificates(user.id) : Promise.resolve([])),
        enabled: !!user?.id,
    })

    const { data: ktpResp } = useQuery({
        queryKey: ['me-ktp', user?.id],
        queryFn: async () => {
            const res = await ktpService.getStatus()
            if (!res.success) throw new Error(res.error?.message || 'Gagal memuat status KTP')
            return res.data as KTPStatusResponse
        },
        enabled: !!user?.id,
    })

    // KTP Form States
    const [ktpNik, setKtpNik] = useState('')
    const [ktpBirth, setKtpBirth] = useState('')
    const [ktpFile, setKtpFile] = useState<File | null>(null)
    const [ktpError, setKtpError] = useState<string | null>(null)
    const [ktpSubmitting, setKtpSubmitting] = useState(false)
    const [reopenForm, setReopenForm] = useState(false)

    const ktpStatus = ktpResp?.status || 'not_submitted'
    const showKtpForm = ktpStatus === 'not_submitted' || (ktpStatus === 'rejected' && reopenForm)
    const isIdentityLocked = ktpStatus === 'verified'

    // Handlers
    const handleAvatarClick = () => fileInputRef.current?.click()

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        setIsUploading(true)
        try {
            const uploadRes = await uploadService.uploadImage(file)
            if (!uploadRes.success) throw new Error(uploadRes.error?.message || 'Gagal upload foto')
            
            const patchRes = await apiClient.patch('/users/me', { avatar_url: uploadRes.data!.url })
            if (!patchRes.success) throw new Error(patchRes.error?.message || 'Gagal update avatar')
            
            await queryClient.invalidateQueries({ queryKey: ['auth-user'] })
            setIdentityStatus({ type: 'success', text: 'Foto profil diperbarui' })
        } catch (err) {
            setIdentityStatus({ type: 'error', text: (err as Error).message })
        } finally {
            setIsUploading(false)
        }
    }

    const handleSaveIdentity = async () => {
        setSavingIdentity(true)
        setIdentityStatus({ type: 'idle' })
        try {
            const payload: Record<string, string> = { name }
            // Only send gender and birth_date if identity is not locked by KTP
            if (!isIdentityLocked) {
                payload.gender = gender
                const cleanBirthDate = birthDate.split('T')[0]
                payload.birth_date = cleanBirthDate
            }
            const res = await apiClient.patch('/users/me', payload)
            if (!res.success) throw new Error(res.error?.message || 'Gagal menyimpan identitas')
            
            // Update global state and localStorage
            updateUser({ name, gender: isIdentityLocked ? user?.gender : (gender || undefined), birthDate: isIdentityLocked ? user?.birthDate : birthDate })
            
            await queryClient.invalidateQueries({ queryKey: ['auth-user'] })
            setIdentityStatus({ type: 'success', text: 'Profil berhasil diperbarui' })
        } catch (err) {
            setIdentityStatus({ type: 'error', text: (err as Error).message })
        } finally {
            setSavingIdentity(false)
        }
    }

    const handleSubmitKTP = async (e: React.FormEvent) => {
        e.preventDefault()
        setKtpError(null)
        if (!/^\d{16}$/.test(ktpNik)) return setKtpError('NIK harus 16 digit angka')
        // Ensure ktpBirth is clean YYYY-MM-DD
        const cleanKtpBirth = ktpBirth.split('T')[0]
        if (!cleanKtpBirth || !ktpFile) return setKtpError('Lengkapi semua data KTP (termasuk Tanggal Lahir sesuai KTP)')
        
        setKtpSubmitting(true)
        try {
            const res = await ktpService.submit(ktpNik, cleanKtpBirth, ktpFile)
            if (!res.success) throw new Error(res.error?.message || 'Gagal mengirim KTP')
            setReopenForm(false)
            await queryClient.invalidateQueries({ queryKey: ['me-ktp', user?.id] })
        } catch (err) {
            setKtpError((err as Error).message)
        } finally {
            setKtpSubmitting(false)
        }
    }

    // Membership query
    const { data: membership } = useMyMembership()
    const { data: thresholds } = useMyThresholds()

    const MembershipSection = () => {
        if (!membership) {
            return (
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 p-10 shadow-[0_20px_50px_rgba(0,0,0,0.02)]">
                    <div className="flex items-center gap-4 mb-8 pb-8 border-b border-slate-50 dark:border-white/5">
                        <div className="size-12 bg-amber-500/10 rounded-2xl flex items-center justify-center">
                            <span className="material-symbols-outlined text-amber-500 text-[24px]">emoji_events</span>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-white leading-none">Membership</h2>
                            <p className="text-sm text-slate-400 font-medium mt-1">Status keanggotaan Anda</p>
                        </div>
                    </div>
                    <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-8 italic">Belum ada membership</p>
                </div>
            )
        }
        return <MembershipCard membership={membership} thresholds={thresholds} />
    }

    return (
        <div className={`min-h-screen bg-slate-50 dark:bg-slate-950 ${compact ? 'pt-0 pb-4' : 'pt-24 md:pt-32 pb-32'}`} data-testid="profile-page">
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
            
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                <div className={`flex flex-col lg:flex-row ${compact ? 'gap-4' : 'gap-12'}`}>
                    {/* Sidebar */}
                    <div className={`${compact ? 'lg:w-[300px]' : 'lg:w-[380px]'} shrink-0 space-y-4`}>
                        <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 shadow-[0_20px_50px_rgba(0,0,0,0.04)] overflow-hidden">
                            <div className={`${compact ? 'h-20' : 'h-40'} bg-gradient-to-br from-primary/30 via-indigo-500/20 to-purple-600/20`} />
                            <div className={`${compact ? 'px-6 pb-6 -mt-10' : 'px-10 pb-10 -mt-20'} text-center`}>
                                <div className={`inline-block relative ${compact ? 'mb-3' : 'mb-6'} group cursor-pointer`} onClick={handleAvatarClick}>
                                    <Avatar
                                        name={user?.name || 'User'}
                                        size={compact ? 'md' : 'lg'}
                                        src={user?.avatar}
                                        className={`${compact ? 'w-20 h-20 text-2xl' : 'w-24 h-24 text-3xl md:w-40 md:h-40 md:text-5xl'} ring-[12px] ring-white dark:ring-slate-900 shadow-2xl transition-all ${isUploading ? 'opacity-50' : 'group-hover:brightness-90'}`}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="size-12 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white">
                                            <span className="material-symbols-outlined">{isUploading ? 'sync' : 'photo_camera'}</span>
                                        </div>
                                    </div>
                                    {ktpStatus === 'verified' && (
                                        <div className="absolute bottom-3 right-3 size-10 bg-green-500 rounded-full border-[5px] border-white dark:border-slate-900 flex items-center justify-center shadow-lg">
                                            <span className="material-symbols-outlined text-white text-[16px] filled">verified</span>
                                        </div>
                                    )}
                                </div>
                                <h1 className={`${compact ? 'text-lg' : 'text-2xl'} font-bold text-slate-900 dark:text-white tracking-tight mb-1`}>{user?.name}</h1>
                                <p className={`text-slate-500 dark:text-slate-400 font-medium ${compact ? 'mb-2 text-xs' : 'mb-6 text-sm'}`}>{user?.email}</p>
                                <div className={`inline-flex px-5 py-2 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 ${compact ? 'text-[9px]' : 'text-[10px]'} font-black rounded-full uppercase tracking-widest border border-slate-200/50 dark:border-white/5`}>
                                    {user?.role || 'Peserta'}
                                </div>
                            </div>
                        </div>

                        <div className={`hidden lg:block bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 ${compact ? 'p-4' : 'p-10'}`}>
                            {isStudent && (
                                <Link
                                    to="/dashboard/payments"
                                    className="w-full flex items-center justify-between p-5 bg-primary/5 dark:bg-primary/5 rounded-2xl hover:bg-primary/10 dark:hover:bg-primary/10 transition-all group mb-3"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="size-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                            <span className="material-symbols-outlined text-primary">receipt_long</span>
                                        </div>
                                        <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Riwayat Pembayaran</span>
                                    </div>
                                    <span className="material-symbols-outlined text-primary/30 text-[18px]">chevron_right</span>
                                </Link>
                            )}
                            <button onClick={() => logout()} className="w-full flex items-center justify-between p-5 bg-rose-50/50 dark:bg-rose-500/5 rounded-2xl hover:bg-rose-100/50 dark:hover:bg-rose-500/10 transition-all group">
                                <div className="flex items-center gap-4">
                                    <div className="size-10 bg-rose-500/10 rounded-xl flex items-center justify-center">
                                        <span className="material-symbols-outlined text-rose-500">logout</span>
                                    </div>
                                    <span className="text-sm font-bold text-rose-600 dark:text-rose-400">Keluar Akun</span>
                                </div>
                                <span className="material-symbols-outlined text-rose-300 text-[18px]">chevron_right</span>
                            </button>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className={`flex-1 ${compact ? 'space-y-4' : 'space-y-8'}`}>
                        {/* Informasi Dasar */}
                        <div className={`bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 ${compact ? 'p-6' : 'p-6 md:p-10 lg:p-12'} shadow-[0_20px_50px_rgba(0,0,0,0.02)]`}>
                            <div className={`flex items-center gap-4 ${compact ? 'mb-4 pb-4' : 'mb-10 pb-8'} border-b border-slate-50 dark:border-white/5`}>
                                <div className={`${compact ? 'size-10' : 'size-12'} bg-primary/10 rounded-2xl flex items-center justify-center`}>
                                    <span className={`material-symbols-outlined text-primary ${compact ? 'text-[20px]' : 'text-[24px]'}`}>manage_accounts</span>
                                </div>
                                <div>
                                    <h2 className={`${compact ? 'text-lg' : 'text-2xl'} font-bold text-slate-900 dark:text-white leading-none`}>Informasi Dasar</h2>
                                    {isStudent && <p className="text-sm text-slate-400 font-medium mt-1">Data ini akan digunakan untuk sertifikat Anda</p>}
                                </div>
                            </div>

                            <div className={`grid md:grid-cols-2 ${compact ? 'gap-x-4 gap-y-3' : 'gap-x-10 gap-y-8'}`}>
                                <div className={isStudent ? 'md:col-span-2 space-y-3' : 'space-y-3'}>
                                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">Nama Lengkap</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full p-4 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-bold focus:border-primary/50 outline-none transition-all"
                                        placeholder="Masukkan nama lengkap"
                                    />
                                </div>
                                {isStudent && (
                                    <>
                                        <div className="space-y-4">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">
                                                Jenis Kelamin
                                                {isIdentityLocked && <span className="ml-2 text-xs font-normal text-slate-400">(dari KTP)</span>}
                                            </label>
                                            <div className="flex gap-4">
                                                {[
                                                    { val: 'L', label: 'Laki-laki', icon: 'male' },
                                                    { val: 'P', label: 'Perempuan', icon: 'female' }
                                                ].map((opt) => (
                                                    <button
                                                        key={opt.val}
                                                        onClick={() => !isIdentityLocked && setGender(opt.val as any)}
                                                        disabled={isIdentityLocked}
                                                        className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-2xl border transition-all ${
                                                            gender === opt.val 
                                                            ? 'bg-primary/5 border-primary text-primary shadow-sm shadow-primary/5' 
                                                            : isIdentityLocked
                                                              ? 'border-slate-100 dark:border-white/5 text-slate-400 opacity-60 cursor-not-allowed'
                                                              : 'border-slate-100 dark:border-white/5 text-slate-500 hover:border-slate-200'
                                                        }`}
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">{opt.icon}</span>
                                                        <span className="text-sm font-bold">{opt.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">
                                                Tanggal Lahir
                                                {isIdentityLocked && <span className="ml-2 text-xs font-normal text-slate-400">(dari KTP)</span>}
                                            </label>
                                            <input
                                                type="date"
                                                value={birthDate}
                                                onChange={(e) => setBirthDate(e.target.value)}
                                                disabled={isIdentityLocked}
                                                className="w-full p-4 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-bold focus:border-primary/50 outline-none transition-all text-slate-700 dark:text-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            {identityStatus.text && (
                                <div className={`mt-8 p-5 rounded-2xl text-sm font-bold flex items-center gap-3 ${
                                    identityStatus.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-rose-50 text-rose-600'
                                }`}>
                                    <span className="material-symbols-outlined text-[20px]">{identityStatus.type === 'success' ? 'check_circle' : 'error'}</span>
                                    {identityStatus.text}
                                </div>
                            )}

                            <button
                                onClick={handleSaveIdentity}
                                disabled={savingIdentity}
                                className={`${compact ? 'mt-4 px-6 py-2.5' : 'mt-10 px-10 py-4'} bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-bold text-sm hover:opacity-90 active:scale-95 transition-all shadow-xl shadow-slate-200`}
                            >
                                {savingIdentity ? 'Menyimpan...' : 'Simpan Perubahan'}
                            </button>
                        </div>

                        {/* KTP Verification Card - only for students */}
                        {isStudent && (
                        <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 p-10 md:p-12 shadow-[0_20px_50px_rgba(0,0,0,0.02)]">
                            <div className={`flex flex-col md:flex-row md:items-center justify-between gap-6 ${
                                (ktpStatus !== 'verified' && ktpStatus !== 'pending') ? 'mb-8 pb-8 border-b border-slate-50 dark:border-white/5' : ''
                            }`}>
                                <div className="flex items-center gap-4">
                                    <div className="size-12 bg-blue-500/10 rounded-2xl flex items-center justify-center">
                                        <span className="material-symbols-outlined text-blue-500 text-[24px]">badge</span>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white leading-none">Verifikasi KTP</h2>
                                        <p className="text-sm text-slate-400 font-medium mt-1">Gunakan untuk akses QR Absensi</p>
                                    </div>
                                </div>

                                {ktpStatus === 'verified' && (
                                    <div className="flex items-center gap-3 bg-green-50 dark:bg-green-500/10 px-6 py-3 rounded-2xl border border-green-100 dark:border-green-500/20">
                                        <div className="size-8 bg-green-500 rounded-lg flex items-center justify-center">
                                            <span className="material-symbols-outlined text-white text-[18px] filled">verified</span>
                                        </div>
                                        <span className="text-sm font-bold text-green-700 dark:text-green-300">Terverifikasi</span>
                                    </div>
                                )}

                                {ktpStatus === 'pending' && (
                                    <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-500/10 px-6 py-3 rounded-2xl border border-amber-100 dark:border-amber-500/20">
                                        <div className="size-8 bg-amber-500 rounded-lg flex items-center justify-center">
                                            <span className="material-symbols-outlined text-white text-[18px]">hourglass_top</span>
                                        </div>
                                        <span className="text-sm font-bold text-amber-700 dark:text-amber-300">Menunggu Review</span>
                                    </div>
                                )}

                                {ktpStatus === 'rejected' && !reopenForm && (
                                    <div className="flex items-center gap-3 bg-rose-50 dark:bg-rose-500/10 px-6 py-3 rounded-2xl border border-rose-100 dark:border-rose-500/20">
                                        <div className="size-8 bg-rose-500 rounded-lg flex items-center justify-center">
                                            <span className="material-symbols-outlined text-white text-[18px]">close</span>
                                        </div>
                                        <span className="text-sm font-bold text-rose-700 dark:text-rose-300">Ditolak</span>
                                    </div>
                                )}
                            </div>

                            {/* Pending Status Detail */}
                            {ktpStatus === 'pending' && (
                                <div className="mt-8 p-6 bg-amber-50/50 dark:bg-amber-500/5 rounded-2xl border border-amber-100 dark:border-amber-500/10">
                                    <div className="flex items-start gap-4">
                                        <div className="size-12 bg-amber-500/10 rounded-2xl flex items-center justify-center shrink-0">
                                            <span className="material-symbols-outlined text-amber-500 text-[24px]">schedule</span>
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-1">KTP Anda Sedang Diproses</h4>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                                Data KTP Anda telah berhasil dikirim dan sedang dalam proses verifikasi oleh admin. 
                                                Proses ini biasanya memakan waktu 1-3 hari kerja. Anda akan menerima notifikasi setelah verifikasi selesai.
                                            </p>
                                            {ktpResp?.submittedAt && (
                                                <p className="text-[10px] font-bold text-slate-400 mt-3">
                                                    Dikirim pada: {new Date(ktpResp.submittedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Rejected Status Detail */}
                            {ktpStatus === 'rejected' && !reopenForm && (
                                <div className="mt-8 p-6 bg-rose-50/50 dark:bg-rose-500/5 rounded-2xl border border-rose-100 dark:border-rose-500/10">
                                    <div className="flex items-start gap-4">
                                        <div className="size-12 bg-rose-500/10 rounded-2xl flex items-center justify-center shrink-0">
                                            <span className="material-symbols-outlined text-rose-500 text-[24px]">gpp_bad</span>
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Verifikasi KTP Ditolak</h4>
                                            {ktpResp?.rejectionReason && (
                                                <p className="text-xs text-rose-600 dark:text-rose-400 leading-relaxed mb-3">
                                                    <span className="font-bold">Alasan:</span> {ktpResp.rejectionReason}
                                                </p>
                                            )}
                                            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-4">
                                                Silakan perbaiki data Anda dan ajukan kembali.
                                            </p>
                                            <button
                                                onClick={() => setReopenForm(true)}
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-bold text-xs hover:opacity-90 active:scale-95 transition-all shadow-lg shadow-primary/20"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">refresh</span>
                                                Ajukan Ulang
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(ktpStatus === 'not_submitted' || showKtpForm) && (
                                <div className="space-y-8 mt-8">
                                    <form onSubmit={handleSubmitKTP} className="space-y-8">
                                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                            <div className="space-y-3">
                                                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">NIK (16 Digit)</label>
                                                <input
                                                    type="text"
                                                    placeholder="3201xxxxxxxxxxxx"
                                                    value={ktpNik}
                                                    onChange={(e) => setKtpNik(e.target.value.replace(/\D/g, '').slice(0, 16))}
                                                    className="w-full p-4 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-bold focus:border-primary/50 outline-none"
                                                />
                                            </div>
                                            <div className="space-y-3">
                                                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">Tanggal Lahir di KTP</label>
                                                <input
                                                    type="date"
                                                    value={ktpBirth}
                                                    onChange={(e) => setKtpBirth(e.target.value)}
                                                    className="w-full p-4 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-bold focus:border-primary/50 outline-none text-slate-700 dark:text-slate-200"
                                                />
                                            </div>
                                            <div className="space-y-3">
                                                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">Unggah Foto KTP</label>
                                                <input
                                                    type="file"
                                                    onChange={(e) => setKtpFile(e.target.files?.[0] ?? null)}
                                                    className="w-full text-sm text-slate-400 file:mr-4 file:py-3.5 file:px-6 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-900 dark:file:bg-white file:text-white dark:file:text-slate-900 hover:file:opacity-90 transition-all"
                                                />
                                            </div>
                                        </div>
                                        {ktpError && (
                                            <div className="flex items-center gap-2 p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl">
                                                <span className="material-symbols-outlined text-rose-500 text-[18px]">error</span>
                                                <p className="text-xs font-bold text-rose-500">{ktpError}</p>
                                            </div>
                                        )}
                                        <button 
                                            type="submit" 
                                            disabled={ktpSubmitting}
                                            className="bg-primary text-white px-10 py-4 rounded-2xl font-bold text-sm shadow-xl shadow-primary/20 hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            {ktpSubmitting ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                                                    Mengirim...
                                                </>
                                            ) : (
                                                <>
                                                    <span className="material-symbols-outlined text-[18px]">verified</span>
                                                    Kirim Verifikasi
                                                </>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            )}
                        </div>
                        )}

                        {/* Membership - only for students */}
                        {isStudent && <MembershipSection />}

                        {/* Certificates & Security */}
                        <div className={isStudent ? `grid md:grid-cols-2 ${compact ? 'gap-4' : 'gap-8'}` : ''}>
                            {/* Certificates Box - only for students */}
                            {isStudent && (
                            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 p-10">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="size-10 bg-amber-500/10 rounded-xl flex items-center justify-center">
                                        <span className="material-symbols-outlined text-amber-500">workspace_premium</span>
                                    </div>
                                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">Sertifikat</h2>
                                </div>
                                <div className="space-y-4">
                                    {certificates.slice(0, 2).map(cert => (
                                        <div key={cert.id} className="p-4 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/10 flex items-center justify-between">
                                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 line-clamp-1">{cert.courseName}</span>
                                            <button onClick={() => window.open(cert.pdfUrl, '_blank')} className="size-8 bg-white dark:bg-slate-800 rounded-lg flex items-center justify-center shadow-sm">
                                                <span className="material-symbols-outlined text-[18px]">download</span>
                                            </button>
                                        </div>
                                    ))}
                                    {certificates.length === 0 && <p className="text-xs text-slate-400 font-medium text-center py-4 italic">Belum ada sertifikat</p>}
                                </div>
                            </div>
                            )}

                            {/* Security Box */}
                            <div className={`bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 ${compact ? 'p-4' : 'p-10'}`}>
                                <div className={`flex items-center gap-3 ${compact ? 'mb-3' : 'mb-8'}`}>
                                    <div className="size-10 bg-blue-500/10 rounded-xl flex items-center justify-center">
                                        <span className="material-symbols-outlined text-blue-500">lock</span>
                                    </div>
                                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">Keamanan</h2>
                                </div>
                                <div className={`${compact ? 'space-y-2' : 'space-y-4'}`}>
                                    <button onClick={() => setEmailModalOpen(true)} className={`w-full ${compact ? 'p-3' : 'p-4'} bg-slate-50 dark:bg-white/5 rounded-2xl text-left hover:bg-slate-100 transition-all`}>
                                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Email Aktif</p>
                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{user?.email}</p>
                                    </button>
                                    <button onClick={() => setPasswordModalOpen(true)} className={`w-full ${compact ? 'p-3' : 'p-4'} bg-slate-50 dark:bg-white/5 rounded-2xl text-left hover:bg-slate-100 transition-all flex items-center justify-between`}>
                                        <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Ubah Password</span>
                                        <span className="material-symbols-outlined text-slate-300 text-[18px]">chevron_right</span>
                                    </button>
                                    {/* TOTP Status */}
                                    <div className={`w-full ${compact ? 'p-3' : 'p-4'} bg-slate-50 dark:bg-white/5 rounded-2xl`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Authenticator App</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                    {user?.totpEnabled
                                                        ? 'Aktif — kode diperlukan saat login'
                                                        : 'Belum aktif — akan diminta saat login berikutnya'}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {user?.role === 'super_admin' && user?.totpEnabled && (
                                                    <button
                                                        onClick={() => { setResetTOTPError(null); setResetTOTPModalOpen(true); }}
                                                        className="px-3 py-1.5 bg-rose-500 text-white text-[11px] font-bold rounded-xl hover:bg-rose-600 active:scale-95 transition-all"
                                                    >
                                                        Atur Ulang
                                                    </button>
                                                )}
                                                <div className={`size-8 rounded-full flex items-center justify-center ${user?.totpEnabled ? 'bg-green-500/10' : 'bg-amber-500/10'}`}>
                                                    <span className={`material-symbols-outlined text-[18px] ${user?.totpEnabled ? 'text-green-500' : 'text-amber-500'}`}>
                                                        {user?.totpEnabled ? 'verified_user' : 'warning'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mobile Logout Button (Hidden on Desktop) */}
                        <div className="lg:hidden bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 p-10 mt-8">
                            {isStudent && (
                                <Link
                                    to="/dashboard/payments"
                                    className="w-full flex items-center justify-between p-5 bg-primary/5 dark:bg-primary/5 rounded-2xl hover:bg-primary/10 dark:hover:bg-primary/10 transition-all group mb-3"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="size-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                            <span className="material-symbols-outlined text-primary">receipt_long</span>
                                        </div>
                                        <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Riwayat Pembayaran</span>
                                    </div>
                                    <span className="material-symbols-outlined text-primary/30 text-[18px]">chevron_right</span>
                                </Link>
                            )}
                            <button onClick={() => logout()} className="w-full flex items-center justify-between p-5 bg-rose-50/50 dark:bg-rose-500/5 rounded-2xl hover:bg-rose-100/50 dark:hover:bg-rose-500/10 transition-all group">
                                <div className="flex items-center gap-4">
                                    <div className="size-10 bg-rose-500/10 rounded-xl flex items-center justify-center">
                                        <span className="material-symbols-outlined text-rose-500">logout</span>
                                    </div>
                                    <span className="text-sm font-bold text-rose-600 dark:text-rose-400">Keluar Akun</span>
                                </div>
                                <span className="material-symbols-outlined text-rose-300 text-[18px]">chevron_right</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Password Modal */}
            {passwordModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md" onClick={() => setPasswordModalOpen(false)} />
                    <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 sm:p-10 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14)] border border-slate-100 dark:border-white/5">
                        <div className="flex items-start gap-5 mb-8">
                            <div className="size-12 bg-primary/5 rounded-2xl flex items-center justify-center shrink-0">
                                <span className="material-symbols-outlined text-primary text-[24px]">lock_reset</span>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Ubah Password</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">Pastikan password baru Anda kuat dan sulit ditebak.</p>
                            </div>
                        </div>
                        
                        <form onSubmit={async (e) => {
                            e.preventDefault();
                            if (passData.newPassword !== passData.confirmPassword) {
                                setPassError("Password baru tidak cocok");
                                return;
                            }
                            setPassSubmitting(true);
                            setPassError(null);
                            try {
                                const res = await apiClient.put('/auth/change-password', passData);
                                if (!res.success) throw new Error(res.error?.message || "Gagal ubah password");
                                setPasswordModalOpen(false);
                                setIdentityStatus({ type: 'success', text: 'Password berhasil diubah' });
                                setPassData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                            } catch (err) { setPassError((err as Error).message) }
                            finally { setPassSubmitting(false) }
                        }} className="space-y-5">
                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Password Sekarang</label>
                                <input
                                    type="password"
                                    required
                                    value={passData.currentPassword}
                                    onChange={(e) => setPassData({...passData, currentPassword: e.target.value})}
                                    className="w-full px-5 py-3.5 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-medium outline-none focus:border-primary/30 focus:bg-white transition-all"
                                    placeholder="••••••••"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Password Baru</label>
                                <input
                                    type="password"
                                    required
                                    value={passData.newPassword}
                                    onChange={(e) => setPassData({...passData, newPassword: e.target.value})}
                                    className="w-full px-5 py-3.5 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-medium outline-none focus:border-primary/30 focus:bg-white transition-all"
                                    placeholder="Min. 8 karakter"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Konfirmasi Password Baru</label>
                                <input
                                    type="password"
                                    required
                                    value={passData.confirmPassword}
                                    onChange={(e) => setPassData({...passData, confirmPassword: e.target.value})}
                                    className="w-full px-5 py-3.5 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-medium outline-none focus:border-primary/30 focus:bg-white transition-all"
                                    placeholder="Ulangi password"
                                />
                            </div>

                            {passError && (
                                <div className="flex items-center gap-2 text-rose-500 bg-rose-500/5 p-3 rounded-xl border border-rose-500/10">
                                    <span className="material-symbols-outlined text-[18px]">error_outline</span>
                                    <span className="text-[11px] font-bold">{passError}</span>
                                </div>
                            )}
                            
                            <div className="flex gap-3 pt-3">
                                <button type="button" onClick={() => setPasswordModalOpen(false)} className="flex-1 px-6 py-4 rounded-2xl font-bold text-sm text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-white/5 transition-all">Batal</button>
                                <button type="submit" disabled={passSubmitting} className="flex-[1.5] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl font-bold text-sm shadow-lg hover:opacity-90 active:scale-95 transition-all">
                                    {passSubmitting ? 'Memproses...' : 'Ubah Password'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Email Modal */}
            {emailModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md" onClick={() => setEmailModalOpen(false)} />
                    <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 sm:p-10 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14)] border border-slate-100 dark:border-white/5">
                        <div className="flex items-start gap-5 mb-8">
                            <div className="size-12 bg-blue-500/5 rounded-2xl flex items-center justify-center shrink-0">
                                <span className="material-symbols-outlined text-blue-500 text-[24px]">alternate_email</span>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Ubah Email</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">Gunakan alamat email baru yang aktif.</p>
                            </div>
                        </div>
                        
                        <form onSubmit={async (e) => {
                            e.preventDefault();
                            setEmailSubmitting(true);
                            setEmailError(null);
                            try {
                                const res = await authService.changeEmail(newEmail);
                                if (!res.success) throw new Error(res.error?.message);
                                setEmailModalOpen(false);
                                navigate(`/verify-email?email=${encodeURIComponent(newEmail)}`);
                            } catch (err) { setEmailError((err as Error).message) }
                            finally { setEmailSubmitting(false) }
                        }} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Email Baru</label>
                                <input
                                    type="email"
                                    required
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    placeholder="nama@emailbaru.com"
                                    className="w-full px-5 py-3.5 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-medium outline-none focus:border-primary/30 focus:bg-white transition-all"
                                />
                            </div>
                            
                            {emailError && (
                                <div className="flex items-center gap-2 text-rose-500 bg-rose-500/5 p-3 rounded-xl border border-rose-500/10">
                                    <span className="material-symbols-outlined text-[18px]">error_outline</span>
                                    <span className="text-[11px] font-bold">{emailError}</span>
                                </div>
                            )}
                            
                            <div className="flex gap-3 pt-3">
                                <button type="button" onClick={() => setEmailModalOpen(false)} className="flex-1 px-6 py-4 rounded-2xl font-bold text-sm text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-white/5 transition-all">Batal</button>
                                <button type="submit" disabled={emailSubmitting} className="flex-[1.5] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl font-bold text-sm shadow-lg hover:opacity-90 active:scale-95 transition-all">
                                    {emailSubmitting ? 'Kirim OTP' : 'Kirim OTP'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reset TOTP Modal */}
            {resetTOTPModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md" onClick={() => setResetTOTPModalOpen(false)} />
                    <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 sm:p-10 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14)] border border-slate-100 dark:border-white/5">
                        <div className="flex items-start gap-5 mb-8">
                            <div className="size-12 bg-rose-500/5 rounded-2xl flex items-center justify-center shrink-0">
                                <span className="material-symbols-outlined text-rose-500 text-[24px]">phonelink_lock</span>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Atur Ulang TOTP</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                    TOTP akan di-reset dan Anda perlu memindai QR code baru saat login berikutnya. Lanjutkan?
                                </p>
                            </div>
                        </div>

                        {resetTOTPError && (
                            <div className="flex items-center gap-2 text-rose-500 bg-rose-500/5 p-3 rounded-xl border border-rose-500/10 mb-6">
                                <span className="material-symbols-outlined text-[18px]">error_outline</span>
                                <span className="text-[11px] font-bold">{resetTOTPError}</span>
                            </div>
                        )}

                        <div className="flex gap-3 pt-3">
                            <button type="button" onClick={() => setResetTOTPModalOpen(false)} className="flex-1 px-6 py-4 rounded-2xl font-bold text-sm text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-white/5 transition-all">Batal</button>
                            <button
                                type="button"
                                disabled={resetTOTPMutation.isPending}
                                onClick={async () => {
                                    setResetTOTPError(null)
                                    if (!user?.id) return
                                    resetTOTPMutation.mutate(user.id, {
                                        onSuccess: (data: any) => {
                                            setResetTOTPModalOpen(false)
                                            updateUser({ ...user, totpEnabled: false })
                                            if (data?.partial_token) {
                                                localStorage.setItem('partialToken', data.partial_token)
                                            }
                                            localStorage.setItem('totpRequiresSetup', 'true')
                                            navigate('/verify-totp')
                                        },
                                        onError: (err) => {
                                            setResetTOTPError(err.message || 'Gagal reset TOTP')
                                        },
                                    })
                                }}
                                className="flex-[1.5] bg-rose-500 text-white px-6 py-4 rounded-2xl font-bold text-sm shadow-lg hover:bg-rose-600 active:scale-95 transition-all"
                            >
                                {resetTOTPMutation.isPending ? 'Mereset...' : 'Reset TOTP'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ProfilePage
